import React, { useState, useEffect } from 'react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CORNERS = {
  FHD: { name: 'FHD (Sun-Tue Days)', days: [0, 1, 2], shift: 'day' },
  FHN: { name: 'FHN (Sun-Tue Nights)', days: [0, 1, 2], shift: 'night' },
  BHD: { name: 'BHD (Thu-Sat Days)', days: [4, 5, 6], shift: 'day' },
  BHN: { name: 'BHN (Thu-Sat Nights)', days: [4, 5, 6], shift: 'night' },
};

// Map LVP casual names to FCLM labor process_path names
const LVP_TO_FCLM = {
  'PID Unload': 'PID Unload', 'PID Divert': 'PID Divert', 'PID Inject': 'PID Inject', 'PID Operator': 'PID Operator',
  'AROS Unload': 'AROS Unload', 'AROS Operator': 'AROS Operator', 'AROS CSX Label': 'AROS CSX Label',
  'AROS Avery': 'AROS Avery', 'AROS Divert': 'AROS Divert', 'AROS Sort': 'AROS Sort', 'AROS Waterspider': 'AROS Waterspider',
  'PR Unload': 'PR Unload', 'Pallet Build': 'Pallet Build', 'Pallet Wrap/Lantech': 'Wrap/Lantech',
  'PR Waterspider': 'PR Waterspider', 'IB Pallet EPJ WS': 'IB Pallet EPJ WS', 'Pallet Auditors': 'Pallet Auditors',
  'PIT Pallet Build': 'PIT Pallet Build', 'IB PIT Waterspider': 'IB PIT Waterspider', 'PIT Unloader': 'PIT Unloader',
  'EOL Avery': 'EOL Avery', 'EOL Divert': 'EOL Divert', 'EOL Sort': 'EOL Sort', 'EOL Waterspider': 'EOL Waterspider',
  'Fluid WS': 'Fluid WS', 'Fluid Load': 'Fluid Load', 'Sorter Wallbuilder': 'Sorter Wallbuild', 'Wall Builder': 'Offline Wallbuild', 'Jam Clear': 'Jam Clear',
  'OB EPJ WaterSpider': 'OB EPJ WaterSpider', 'Pallet Loader': 'Pallet Loader', 'OB PIT Operator': 'OB PIT Operator', 'OB EPJ Operator': 'OB EPJ Operator',
  'IB TDR': 'IB TDR', 'IB Clerk': 'IB Clerk', 'IB Problem Solve': 'IB Problem Solve', 'IB Lead/PA': 'IB Lead/PA',
  'DA Problem Solve': 'DA Problem Solve', 'DA Lead/PA': 'DA Lead/PA', 'OB Training': 'OB Training', 'OB Ambassador': 'OB Ambassador',
  'Ship Clerk': 'Ship Clerk', 'OB TDR': 'OB TDR', 'Trans Out Overflow': 'Trans Out Overflow',
};

const DEPTS = [
  { id: 'pid', name: 'PID', group: 'PID', functions: ['PID Unload', 'PID Divert', 'PID Inject', 'PID Operator'] },
  { id: 'aros', name: 'AROS Inbound', group: 'AROS', functions: ['AROS Unload', 'AROS Operator', 'AROS CSX Label'] },
  { id: 'ibPR', name: 'IB PR', group: 'IB PR', functions: ['PR Unload', 'Pallet Build', 'Pallet Wrap/Lantech', 'PR Waterspider', 'IB Pallet EPJ WS', 'Pallet Auditors'] },
  { id: 'ibPIT', name: 'IB PIT', group: 'IB PIT', functions: ['PIT Pallet Build', 'IB PIT Waterspider', 'PIT Unloader'] },
  // Fused EOL/AROS department: EOL + outbound AROS. EOL Sort + AROS Sort together
  // are the Manual Sort line (presented separately as function rows; the dept
  // total is the fused figure). Frontend-only aggregation over unchanged backend.
  { id: 'eolAros', name: 'EOL/AROS', group: 'EOL/AROS', functions: ['EOL Avery', 'EOL Divert', 'EOL Sort', 'EOL Waterspider', 'AROS Avery', 'AROS Divert', 'AROS Sort', 'AROS Waterspider'] },
  { id: 'fluid', name: 'Fluid', group: 'Fluid', functions: ['Fluid WS', 'Fluid Load', 'Sorter Wallbuilder', 'Wall Builder', 'Jam Clear'] },
  { id: 'obPR', name: 'OBPR', group: 'OBPR', functions: ['OB EPJ WaterSpider', 'Pallet Loader', 'OB PIT Operator', 'OB EPJ Operator'] },
  { id: 'ibSupport', name: 'IB Support', group: 'IB Support', functions: ['IB TDR', 'IB Clerk', 'IB Problem Solve', 'IB Lead/PA'] },
  { id: 'obSupport', name: 'OB Support', group: 'OB Support', functions: ['Ship Clerk', 'OB TDR', 'OB Training', 'OB Ambassador', 'DA Problem Solve', 'DA Lead/PA', 'Trans Out Overflow'] },
];

export default function ShiftHistory({ api, site }) {
  const [weekStart, setWeekStart] = useState('');
  const [corner, setCorner] = useState('FHD');
  const [planData, setPlanData] = useState(null);
  const [actuals, setActuals] = useState({});
  const [midiData, setMidiData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const now = new Date();
    const pacificOffset = -7 * 60;
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const pacNow = new Date(utcMs + pacificOffset * 60000);
    const day = pacNow.getDay();
    const sunday = new Date(pacNow);
    sunday.setDate(pacNow.getDate() - day);
    const y = sunday.getFullYear();
    const m = String(sunday.getMonth() + 1).padStart(2, '0');
    const d2 = String(sunday.getDate()).padStart(2, '0');
    setWeekStart(`${y}-${m}-${d2}`);
  }, []);


  useEffect(() => {
    if (!weekStart) return;
    setLoading(true);
    fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${weekStart}&shift_type=plan`)
      .then(r => r.json()).then(data => { if (data?.shiftData) setPlanData(data); else if (data?.data?.shiftData) setPlanData(data.data); else setPlanData(null); }).catch(() => setPlanData(null));

    const c = CORNERS[corner];
    const promises = c.days.map(dayIdx => {
      const dateObj = new Date(weekStart + 'T12:00:00'); dateObj.setDate(dateObj.getDate() + dayIdx);
      const dateStr = dateObj.toISOString().split('T')[0];
      return fetch(`${api}/api/fclm/cache?site=${site}&date=${dateStr}&shift_type=${c.shift}`)
        .then(r => r.json()).then(data => ({ dayIdx, data })).catch(() => ({ dayIdx, data: null }));
    });
    Promise.all(promises).then(results => {
      const a = {}; results.forEach(({ dayIdx, data }) => { a[dayIdx] = data; });
      setActuals(a); setLoading(false);
    });

    // Fetch MIDI plan + actual for each day for FIFO compliance
    const midiPromises = c.days.map(dayIdx => {
      const dateObj = new Date(weekStart + 'T12:00:00'); dateObj.setDate(dateObj.getDate() + dayIdx);
      const dateStr = dateObj.toISOString().split('T')[0];
      return Promise.all([
        fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_PLAN_${dateStr}&shift_type=${c.shift}`).then(r => r.json()).catch(() => null),
        fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_ACTUAL_${dateStr}&shift_type=${c.shift}`).then(r => r.json()).catch(() => null),
      ]).then(([plan, actual]) => ({ dayIdx, plan, actual }));
    });
    Promise.all(midiPromises).then(results => {
      const m = {}; results.forEach(({ dayIdx, plan, actual }) => { m[dayIdx] = { plan, actual }; });
      setMidiData(m);
    });
  }, [api, site, weekStart, corner]);

  const c = CORNERS[corner];
  const shiftKeys = c.days.map(d => DAYS[d] + '_' + c.shift);

  const getPlanHours = (shiftKey) => planData?.shiftData?.[shiftKey]?.hours || 0;
  const getPlanCartons = (shiftKey) => planData?.shiftData?.[shiftKey]?.cartons || 0;

  // Get planned function hours - directly from saved computedHours
  const getPlanFnHours = (casualName, shiftKey) => {
    return planData?.computedHours?.[casualName]?.[shiftKey] || 0;
  };

  // Get planned dept hours
  const getPlanDeptHours = (dept, shiftKey) => {
    return planData?.computedHours?.[dept.name]?.[shiftKey] || dept.functions.reduce((s, fn) => s + getPlanFnHours(fn, shiftKey), 0);
  };

  // Get actual function hours from FCLM labor array
  const getActualFnHours = (dayIdx, casualName) => {
    const data = actuals[dayIdx];
    if (!data || !data.labor) return 0;
    const fclmName = LVP_TO_FCLM[casualName] || casualName;
    const match = data.labor.find(l => l.process_path === fclmName);
    return match?.total || 0;
  };

  const getActualDeptHours = (dayIdx, dept) => dept.functions.reduce((s, fn) => s + getActualFnHours(dayIdx, fn), 0);
  const getActualCartons = (dayIdx) => { const d = actuals[dayIdx]; return d?.cartons?.inbound?.total || d?.total_brt || 0; };
  const getActualTotalHours = (dayIdx) => { const d = actuals[dayIdx]; if (!d?.labor) return 0; return d.labor.reduce((s, l) => s + (l.total||0), 0); };

  const cellStyle = { padding: '2px 4px', textAlign: 'center', fontSize: '0.6rem', fontWeight: 500, border: '1px solid #ccc', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };
  const deptStyle = { ...cellStyle, textAlign: 'left', fontWeight: 700, background: '#e8f5e9', position: 'sticky', left: 0, zIndex: 1 };
  const fnStyle = { ...cellStyle, textAlign: 'left', paddingLeft: 12, fontSize: '0.55rem', position: 'sticky', left: 0, background: '#fff', zIndex: 1 };

  const delta = (plan, actual) => { if (!plan) return '—'; return ((actual - plan) / plan * 100).toFixed(0) + '%'; };
  const dColor = (plan, actual, moreIsBad) => { if (!plan || !actual) return '#666'; const diff = actual - plan; return (moreIsBad ? diff > 0 : diff >= 0) ? '#ef5350' : '#4caf50'; };

  const renderRow = (label, style, getPlanFn, getActualFn, moreIsBad) => (
    <tr>
      <td style={style}>{label}</td>
      {c.days.map((dayIdx, i) => {
        const plan = getPlanFn(i);
        const actual = getActualFn(dayIdx);
        const diff = actual - plan;
        return (
          <React.Fragment key={dayIdx}>
            <td style={cellStyle}>{plan ? Math.round(plan).toLocaleString() : '—'}</td>
            <td style={cellStyle}>{actual ? Math.round(actual).toLocaleString() : '—'}</td>
            <td style={{ ...cellStyle, color: dColor(plan, actual, moreIsBad) }}>{delta(plan, actual)}</td>
            <td style={{ ...cellStyle, borderRight: '2px solid #000', color: dColor(plan, actual, moreIsBad), fontWeight: 600 }}>{plan && actual ? (diff > 0 ? '+' : '') + Math.round(diff).toLocaleString() : '—'}</td>
          </React.Fragment>
        );
      })}
      {(() => {
        const planT = c.days.reduce((s, _, i) => s + getPlanFn(i), 0);
        const actualT = c.days.reduce((s, d) => s + getActualFn(d), 0);
        const diff = actualT - planT;
        return <>
          <td style={{ ...cellStyle, fontWeight: 700 }}>{planT ? Math.round(planT).toLocaleString() : '—'}</td>
          <td style={{ ...cellStyle, fontWeight: 700 }}>{actualT ? Math.round(actualT).toLocaleString() : '—'}</td>
          <td style={{ ...cellStyle, fontWeight: 700, color: dColor(planT, actualT, moreIsBad) }}>{delta(planT, actualT)}</td>
          <td style={{ ...cellStyle, fontWeight: 700, color: dColor(planT, actualT, moreIsBad) }}>{planT && actualT ? (diff > 0 ? '+' : '') + Math.round(diff).toLocaleString() : '—'}</td>
        </>;
      })()}
    </tr>
  );

  return (
    <div>
      <h2 className="section-title">Plan vs Actual — {site}</h2>
      <div className="card">
        <div className="form-row">
          <div className="form-group"><label>Week</label><input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} /></div>
          <div className="form-group"><label>Corner</label><select value={corner} onChange={e => setCorner(e.target.value)}>{Object.entries(CORNERS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}</select></div>
        </div>
        {!planData && !loading && <p style={{ fontSize: '0.75rem', color: '#999' }}>No LVP plan saved for this week.</p>}
      </div>

      {!loading && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: 'max-content' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', minWidth: 130, position: 'sticky', left: 0, zIndex: 2, background: '#232f3e' }}></th>
                {c.days.map(dayIdx => <th key={dayIdx} colSpan={4} style={{ ...headerStyle, borderRight: '2px solid #000' }}>{DAYS[dayIdx].slice(0,3)}</th>)}
                <th colSpan={4} style={headerStyle}>Total</th>
              </tr>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, background: '#232f3e' }}></th>
                {[...c.days, 'total'].map((_, i) => (
                  <React.Fragment key={i}>
                    <th style={headerStyle}>Plan</th><th style={headerStyle}>Act</th><th style={headerStyle}>Δ%</th><th style={{ ...headerStyle, borderRight: i < c.days.length ? '2px solid #000' : 'none' }}>+/-</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Cartons */}
              {renderRow('Total Cartons', { ...cellStyle, textAlign: 'left', fontWeight: 700, position: 'sticky', left: 0, background: '#fff', zIndex: 1 },
                (i) => getPlanCartons(shiftKeys[i]), (dayIdx) => getActualCartons(dayIdx), false)}
              {/* Total Hours */}
              {renderRow('Total Hours', { ...cellStyle, textAlign: 'left', fontWeight: 700, position: 'sticky', left: 0, background: '#fff', zIndex: 1, borderBottom: '2px solid #000' },
                (i) => getPlanHours(shiftKeys[i]), (dayIdx) => getActualTotalHours(dayIdx), true)}

              {/* Dept + Function rows */}
              {DEPTS.map(dept => (
                <React.Fragment key={dept.id}>
                  {renderRow(`${dept.name} Hours`, deptStyle,
                    (i) => getPlanDeptHours(dept, shiftKeys[i]), (dayIdx) => getActualDeptHours(dayIdx, dept), true)}
                  {dept.functions.map(fn => (
                    renderRow(fn, fnStyle, (i) => getPlanFnHours(fn, shiftKeys[i]), (dayIdx) => getActualFnHours(dayIdx, fn), true)
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* FIFO Trailer Compliance */}
      {planData && !loading && (() => {
        const PATHS = ['PID', 'PR', 'MANR', 'SKID'];
        const WEIGHTS = { PID: 10, PR: 2, MANR: 1, SKID: 2 };
        const BUFFER_HOURS = 3;
        const PID_DOORS = /^DD3[4-9]\d|^DD[4-9]\d\d/; // PID doors are DD3xx-DD4xx range

        const calcFifo = (dayIdx) => {
          const md = midiData[dayIdx];
          if (!md?.plan?.planTrailers || !md?.actual?.completions) return null;

          const planTrailers = md.plan.planTrailers;
          const completions = md.actual.completions;
          const planIds = new Set(planTrailers.map(t => (t.visitId || '').replace(/^V/, '')));

          // Determine effective path: PR/MANR/SKID on a PID door counts as PID completion
          // but credit goes to original path for plan compliance
          const getEffectivePath = (comp) => {
            const loc = comp.location || '';
            const origPath = comp.path;
            // If PR/MANR/SKID completed on PID door, it's a bonus for PID
            if ((origPath === 'PR' || origPath === 'MANR' || origPath === 'SKID') && PID_DOORS.test(loc)) {
              return { effectivePath: 'PID', creditPath: origPath, isPidBonus: true };
            }
            return { effectivePath: origPath, creditPath: origPath, isPidBonus: false };
          };

          const results = {};
          for (const path of PATHS) {
            const pathPlan = planTrailers.filter(t => t.path === path);
            // Completions for this path: original path OR upgraded to PID
            const pathComps = completions.filter(c => {
              const { creditPath } = getEffectivePath(c);
              return creditPath === path;
            });

            if (pathComps.length === 0) { results[path] = { fifo: null, planCompliance: null, done: 0, planned: pathPlan.length }; continue; }

            // For FIFO: use all planned trailers of this path, sort oldest first
            const allSorted = [...pathPlan].sort((a, b) => (b.dwellHours || 0) - (a.dwellHours || 0));

            const N = pathComps.length;
            const cutoffIdx = Math.min(N, allSorted.length) - 1;
            const cutoffAge = cutoffIdx >= 0 ? (allSorted[cutoffIdx]?.dwellHours || 0) : 0;

            let compliant = 0;
            for (const comp of pathComps) {
              const { isPidBonus } = getEffectivePath(comp);
              // PID bonus trailers always count as compliant (reward)
              if (isPidBonus) { compliant++; continue; }
              if ((comp.dwellHours || 0) >= cutoffAge - BUFFER_HOURS) compliant++;
            }

            // Plan compliance: completions that were in the plan (or PID bonus = always in-plan)
            let inPlan = 0;
            for (const comp of pathComps) {
              const { isPidBonus } = getEffectivePath(comp);
              if (isPidBonus) { inPlan++; continue; }
              const cId = (comp.visitId || '').replace(/^V/, '');
              if (planIds.has(cId)) inPlan++;
            }

            results[path] = {
              fifo: Math.round(compliant / pathComps.length * 100),
              planCompliance: pathPlan.length > 0 ? Math.round(inPlan / Math.min(pathComps.length, pathPlan.length) * 100) : null,
              done: pathComps.length,
              planned: pathPlan.length,
            };
          }

          // Weighted overall (invisible weights)
          let fifoWSum = 0, fifoWTotal = 0, planWSum = 0, planWTotal = 0;
          for (const path of PATHS) {
            const r = results[path];
            if (r.fifo !== null) { fifoWSum += r.fifo * r.done * WEIGHTS[path]; fifoWTotal += r.done * WEIGHTS[path]; }
            if (r.planCompliance !== null) { planWSum += r.planCompliance * r.done * WEIGHTS[path]; planWTotal += r.done * WEIGHTS[path]; }
          }
          results.overall = {
            fifo: fifoWTotal > 0 ? Math.round(fifoWSum / fifoWTotal) : null,
            planCompliance: planWTotal > 0 ? Math.round(planWSum / planWTotal) : null,
          };
          return results;
        };

        const fifoResults = c.days.map(d => ({ dayIdx: d, results: calcFifo(d) }));
        const pctColor = (v) => v == null ? '#666' : v >= 90 ? '#2e7d32' : v >= 75 ? '#f57f17' : '#c62828';
        const thStyle = { padding: '4px 8px', border: '1px solid #ddd', fontSize: '0.7rem', background: '#333', color: '#fff', textAlign: 'center' };
        const tdStyle = { padding: '4px 8px', border: '1px solid #ddd', fontSize: '0.75rem', textAlign: 'center' };

        return (
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: '0.85rem', margin: '0 0 8px' }}>🚚 Trailer FIFO & Plan Compliance</h3>
            <p style={{ fontSize: '0.6rem', color: '#666', margin: '0 0 8px' }}>
              FIFO% = oldest trailers processed first (3hr buffer). Plan% = completions that were in the shift plan.
              PR/MANR/SKID completed on PID doors count positively for both metrics.
            </p>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Path</th>
                  {fifoResults.map(({ dayIdx }) => (
                    <React.Fragment key={dayIdx}>
                      <th style={thStyle}>{DAYS[dayIdx].slice(0,3)} FIFO%</th>
                      <th style={{ ...thStyle, borderRight: '2px solid #000' }}>{DAYS[dayIdx].slice(0,3)} Plan%</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...PATHS, 'Overall'].map(path => (
                  <tr key={path} style={path === 'Overall' ? { borderTop: '2px solid #000', fontWeight: 700 } : {}}>
                    <td style={{ ...tdStyle, textAlign: 'left', fontWeight: path === 'Overall' ? 700 : 400 }}>{path}</td>
                    {fifoResults.map(({ dayIdx, results }) => {
                      const r = results ? (path === 'Overall' ? results.overall : results[path]) : null;
                      if (!r) return <React.Fragment key={dayIdx}><td style={tdStyle}>—</td><td style={{ ...tdStyle, borderRight: '2px solid #000' }}>—</td></React.Fragment>;
                      return (
                        <React.Fragment key={dayIdx}>
                          <td style={{ ...tdStyle, color: pctColor(r.fifo), fontWeight: 600 }}>{r.fifo != null ? r.fifo + '%' : '—'}</td>
                          <td style={{ ...tdStyle, borderRight: '2px solid #000', color: pctColor(r.planCompliance), fontWeight: 600 }}>{r.planCompliance != null ? r.planCompliance + '%' : '—'}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}
